import React, { useState, useEffect } from 'react'
import { selectModal, setModal } from "../../redux/modalSlice";
import { useDispatch, useSelector } from 'react-redux'
import { reactSelectClassNames } from "../../helpers/react-select-styles";
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { useRequest } from "../../hooks/UseRequest";
import { setNotification } from "../../redux/notificationSlice";
import { generateErrorBody } from "../../helpers/errorHandling";
import Loading from "../../components/Loading";
import { selectPortfolios } from "../../redux/portfolioSlice";
import { selectUser } from "../../redux/userSlice";

export default function ReportArgumentModal() {
  const [saving, setSaving] = useState(false)
  const modal = useSelector(selectModal)
  const jobInfo = modal.options?.jobInfo;
  const requiredFields = jobInfo?.optional_value_2?.split(',');
  const optionFields = jobInfo?.optional_value_3?.split(',');
  const successCallback = modal.options?.successCallback;
  const dispatch = useDispatch();
  const request = useRequest()
  const today = new Date().toISOString().split('T')[0];
  const portfolios = useSelector(selectPortfolios)
  const active = useSelector(selectUser)

  const { register, handleSubmit, control, reset, setError, setValue, formState: { errors, isDirty }, watch } = useForm();

  // Add this helper function to get current month in YYYY-MM format
  const getCurrentMonth = () => {
    return new Date().toISOString().slice(0, 7); // Returns YYYY-MM
  }

  // Update useEffect to handle monthly dates correctly
  useEffect(() => {
    if (jobInfo?.code_key === 'job_daily_lending_summary_report') {
      // Set start date to 30 days ago
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      setValue('start_date', startDate.toISOString().split('T')[0]);
      // Set end date to today
      setValue('end_date', today);
    } else if (jobInfo?.code_key === 'job_monthly_lending_summary_report') {
      // Set start month to 12 months ago
      const startMonth = new Date();
      startMonth.setMonth(startMonth.getMonth() - 12);
      setValue('start_month', startMonth.toISOString().slice(0, 7)); // Format: YYYY-MM
      // Set end month to current month
      setValue('end_month', new Date().toISOString().slice(0, 7));
    }
  }, [jobInfo?.code_key, setValue, today]);

  const dispatchError = () => {
    if (Object.keys(errors).length > 0) {
      const body = generateErrorBody(errors, [])
      dispatch(setNotification({ type: 'error', body: body }))
    }
  }

  const periodOptions = [
    { value: 'total', label: 'Total' },
    { value: 'daily', label: 'Daily' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' }
  ]

  const reportTypeOptions = [
    { value: 'netsuite', label: 'Netsuite' },
    { value: 'tranche', label: 'Tranche' }
  ]

  const onSubmit = async (formData) => {
    setSaving(true);
    let formError = false;

    // Configure the API endpoint
    let endpoint = `${process.env.REACT_APP_API_DOMAIN}/report`;
    if (jobInfo.optional_value_4 === "TRIBAL_ONLY") {
      endpoint = `${process.env.REACT_APP_API_DOMAIN}/tribal_report`;
    }
    request.setEndpoint(endpoint);

    try {
      requiredFields.map((field, index) => {
        if (field && !formData[field]) {
          setError(field, { type: "manual" });
          formError = true;
        }
      })

      if (formData?.start_date && formData?.end_date && (new Date(formData.end_date)) < (new Date(formData.start_date))) {
        setError("end_date", { type: "manual", message: "End Date must be after Start Date" });
        formError = true;
      }

      if (formData?.start_month && formData?.end_month && (new Date(formData.end_month)) < (new Date(formData.start_month))) {
        setError("end_month", { type: "manual", message: "End Month must be after Start Month" });
        formError = true;
      }

      if (formError === true) {
        setSaving(false)
        dispatchError()
        return
      }

      if (formData.portfolio_key?.label === 'All Portfolios') {
        formData.portfolio_key.value = 'All';
      }

      const response = await request.post({
        job_dropdown_option_id: jobInfo.id,
        start_date: formData.start_date,
        end_date: formData.end_date,
        period: formData.period?.value,
        portfolio_key: formData.portfolio_key?.value,
        report_type: formData.report_type?.value,
        customer_id: formData.customer_id,
        loan_id: formData.loan_id,
        download_file: formData.download_file,
        start_month: formData.start_month,
        end_month: formData.end_month,
      });
      if (response.status === 201) {
        successCallback(jobInfo.id, response.data);
        setSaving(false);
        handleCancel();
      } else {
        // If the operation was not successful, show an error notification
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setSaving(false);
      }
    } catch (error) {
      // If an exception occurs, reset the saving state and show an error notification
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }
  const handleCancel = () => {
    reset();
    dispatch(setModal({ is_active: false }));
  };

  // Used for Non System Dropdowns
  const parseLabel = ((field) => {
    let option = field.name;
    if (field.label) {
      option = field.label;
    }
    return option
  })

  return modal.is_active && modal.type === 'report_date_range_modal' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="report_argument_form">
      <div className="modal is-active is-large">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Arguments for {jobInfo?.name}
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section className="modal-card-body  pt-1 pb-1" style={{ overflow: 'hidden' }}>
            {(requiredFields.includes('portfolio_key') || optionFields.includes('portfolio_key')) && (
              <div className="column">
                <h3>Portfolio</h3>
                <div className="mt-1">
                  <Controller
                    name="portfolio_key"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        {...register('portfolio_key')}
                        options={jobInfo?.code_key != "job_tranche_report" ? portfolios.data.filter((o) => ((o.id == null && !active.filtered_portfolio) || o.is_active == 1)).map((o) => ({ label: o.name, value: o.id, code_key: o.code_key }))
                          : portfolios.data.filter((o) => o.id != null && o.is_active == 1).map((o) => ({ label: o.name, value: o.id, code_key: o.code_key }))}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        getOptionLabel={(e) => parseLabel(e)}
                        getOptionValue={(e) => parseLabel(e)}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.code_key })}
                        menuPosition="fixed"
                        form="report_argument_form"
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.portfolio_key && <span>{errors.portfolio_key.message ? errors.portfolio_key.message : "Portfolio is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('period') || optionFields.includes('period')) && (
              <div className="column">
                <h3>Periods</h3>
                <div className="mt-1">
                  <Controller
                    name="period"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        {...register('period')}
                        options={periodOptions}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                        menuPosition="fixed"
                        form="report_argument_form"
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.period && <span>{errors.period.message ? errors.period.message : "Period is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('report_type') || optionFields.includes('report_type')) && (
              <div className="column">
                <h3>Report Type</h3>
                <div className="mt-1">
                  <Controller
                    name="report_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        {...register('report_type')}
                        options={reportTypeOptions}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                        menuPosition="fixed"
                        form="report_argument_form"
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.report_type && <span>{errors.report_type.message ? errors.report_type.message : "Report Type is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('start_date') || optionFields.includes('start_date')) && (
              <div className="column">
                <h3>Start Date</h3>
                <div className="mt-1">
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <input
                        name="start_date"
                        className="input is-small"
                        type="date"
                        form="report_argument_form"
                        max={today}
                        {...register('start_date')}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.start_date && <span>{errors.start_date.message ? errors.start_date.message : "Start Date is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('end_date') || optionFields.includes('end_date')) && (
              <div className="column">
                <h3>End Date</h3>
                <div className="mt-1">
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <input
                        name="end_date"
                        className="input is-small"
                        type="date"
                        max={today}
                        form="report_argument_form"
                        {...register('end_date')}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.end_date && <span>{errors.end_date.message ? errors.end_date.message : "End Date is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('start_month') || optionFields.includes('start_month')) && (
              <div className="column">
                <h3>Start Month</h3>
                <div className="mt-1">
                  <Controller
                    name="start_month"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <input
                        name="start_month"
                        className="input is-small"
                        type="month"
                        form="report_argument_form"
                        max={getCurrentMonth()}
                        value={value || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.start_month && <span>{errors.start_month.message ? errors.start_month.message : "Start Month is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('end_month') || optionFields.includes('end_month')) && (
              <div className="column">
                <h3>End Month</h3>
                <div className="mt-1">
                  <Controller
                    name="end_month"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <input
                        name="end_month"
                        className="input is-small"
                        type="month"
                        max={getCurrentMonth()}
                        form="report_argument_form"
                        value={value || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.end_month && <span>{errors.end_month.message ? errors.end_month.message : "End Month is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('customer_id') || optionFields.includes('customer_id')) && (
              <div className="column">
                <h3>Customer ID</h3>
                <div className="mt-1">
                  <Controller
                    name="customer_id"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <input
                        name="customer_id"
                        className="input is-small"
                        type="text"
                        form="report_argument_form"
                        {...register('customer_id')}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.customer_id && <span>{errors.customer_id.message ? errors.customer_id.message : "Customer ID is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('loan_id') || optionFields.includes('loan_id')) && (
              <div className="column">
                <h3>Loan ID</h3>
                <div className="mt-1">
                  <Controller
                    name="loan_id"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <input
                        name="loan_id"
                        className="input is-small"
                        type="text"
                        form="report_argument_form"
                        {...register('loan_id')}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.loan_id && <span>{errors.loan_id.message ? errors.loan_id.message : "Loan ID is required"}</span>}
                  </p>
                </div>
              </div>
            )}
            {(requiredFields.includes('download_file') || optionFields.includes('download_file')) && (
              <div className="column">
                <h3>Download File Name</h3>
                <div className="mt-1">
                  <Controller
                    name="download_file"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <input
                        name="download_file"
                        className="input is-small"
                        type="text"
                        form="report_argument_form"
                        {...register('download_file')}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.loan_id && <span>{errors.loan_id.message ? errors.loan_id.message : "Download file name is required"}</span>}
                  </p>
                </div>
              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="submit"
                className="button is-link has-text-white is-small ml-2"
                disabled={saving && 'disabled'}
              >
                <span className="mx-3">
                  {saving ? <span> Creating <Loading size={'fa-xs'} noWrapper /> </span> : 'Generate'}
                </span>
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="button is-small has-background-grey has-text-white"
              >
                Cancel
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : ''
}
